import { useEffect, useMemo, useState } from "react";
import { PropsWithChildren } from "preact/compat";
import { createContext } from "preact/compat";
import { useContext, useRef } from "preact/hooks";

export enum LayoutTypes {
  TYPE_DESKTOP = "desktop",
  TYPE_MOBILE = "mobile",
}

export type LayoutType = LayoutTypes.TYPE_DESKTOP | LayoutTypes.TYPE_MOBILE;

export function getLayoutType(width: number): LayoutType {
  if (width <= 768) {
    return LayoutTypes.TYPE_MOBILE;
  }

  return LayoutTypes.TYPE_DESKTOP;
}

export enum LayoutStyleTypes {
  TYPE_XXS = "xxs",
  TYPE_XS = "xs",
  TYPE_SM = "sm",
  TYPE_MD = "md",
  TYPE_LG = "lg",
  TYPE_XL = "xl",
  TYPE_XXL = "xxl",
}

export type LayoutStyleType =
  | LayoutStyleTypes.TYPE_XXS
  | LayoutStyleTypes.TYPE_XS
  | LayoutStyleTypes.TYPE_SM
  | LayoutStyleTypes.TYPE_MD
  | LayoutStyleTypes.TYPE_LG
  | LayoutStyleTypes.TYPE_XL
  | LayoutStyleTypes.TYPE_XXL;

export function getLayoutStyleType(width: number): LayoutStyleType {
  if (width <= 360) {
    return LayoutStyleTypes.TYPE_XXS;
  }
  if (width <= 480) {
    return LayoutStyleTypes.TYPE_XS;
  }
  if (width <= 640) {
    return LayoutStyleTypes.TYPE_SM;
  }
  if (width <= 768) {
    return LayoutStyleTypes.TYPE_MD;
  }
  if (width <= 1024) {
    return LayoutStyleTypes.TYPE_LG;
  }
  if (width <= 1280) {
    return LayoutStyleTypes.TYPE_XL;
  }
  return LayoutStyleTypes.TYPE_XXL;
}

type LayoutContextType = {
  layoutType: LayoutType;
  layoutStyleType: string;
  defaultLayoutStyleType: string;
};

const LayoutContext = createContext<LayoutContextType>(null!);

export const LayotProvider = ({ children }: PropsWithChildren) => {
  const initialWidth = window.innerWidth;
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const currentWidthRef = useRef(initialWidth);

  const [state, setState] = useState({ width: initialWidth });

  const value = useMemo(() => {
    const layoutType = getLayoutType(state.width);
    const layoutStyleType = getLayoutStyleType(state.width);

    // Default layout style type based on layout type, in case of desktop it defaults to 'lg', otherwise to 'sm'.
    // For another specific logic, you can use `layoutStyleType` for auto definition, or specially select necessary type from `LayoutStyleType`.
    const defaultLayoutStyleType = layoutType === LayoutTypes.TYPE_DESKTOP ? LayoutStyleTypes.TYPE_LG : LayoutStyleTypes.TYPE_SM;

    return { layoutType, layoutStyleType, defaultLayoutStyleType };
  }, [state.width]);

  useEffect(() => {
    const debouncedSetState = (newWidth: number) => {
      currentWidthRef.current = newWidth;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setState({ width: currentWidthRef.current });
      }, 150);
    };

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target.tagName === "HTML") {
          debouncedSetState(window.innerWidth);
        }
      }
    });
    const elemSizeObservable = document.querySelector("html")!;

    resizeObserver.observe(elemSizeObservable);
    return () => resizeObserver.unobserve(elemSizeObservable);
  }, []);

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

export const useLayoutType = () => {
  return useContext(LayoutContext);
};
